import * as React from 'react';
import { observer } from 'mobx-react';
import { PageGiveViewModel } from './page-give-view-model';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { Form } from '../../primitives/form';
import { SubmitButton } from '../../components/submit-button/submit-button';
import { FormControl } from '../../components/form-control/form-control';
import { FormControlSplitButton } from '../../components/split-button';
import { DatePickerField } from '../../components/form-fields/date-picker-field';
import { SelectField } from '../../components/form-fields';
import { FormControlNumber } from '../../components/number-input';
import { Formatter } from '../../../Shared/utils/formatter';
import { EndType, FrequencyCode, PaymentLabel } from '../../webgiving-generated';
import { FormField } from '../../components/form-field';
import errorSvg from '../../assets/svg/error.svg';
import { SvgIcon } from '../../components/svg-icon';
import { FadeTransition, fadeTransitionDuration } from '../../components/fade-transition';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { getPageClasses } from '../helpers/get-page-classes';
import { getSizeName } from '../../components/amount-display/amount-display';
import { FormControlFrequencyComboButton } from '../../components/frequency-combo-button/frequency-combo-button';
import { classNames } from '../../../Shared/utils/classnames';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import * as style from './page-give.less';
import { pageFooter } from '../../main/webgiving-main.less';
import { OriginalPassiveAmountInput } from '../../components/amount-input';
import { PanelFooter } from '../../components/panel-footer';
import { OptionalDate } from '../../components/date-picker/date-picker';
import { Text } from '../../components/text';
import { PageGiveDollars } from './page-give-dollars';
import { PageGiveOther } from './page-give-other';
import { getBrandedButtonClass, ButtonStyle } from '../../primitives/button/button';
import { Link } from '../../primitives/link/link';

export { PageGiveViewModel };

@injectWebGivingProps
@observer
export class PageGive extends React.Component<
	{
		vm: PageGiveViewModel;
		transitionState: string;
	} & InjectableWebGivingProps
> {
	panelBodyNode: HTMLDivElement;
	contentNode: HTMLDivElement;

	componentDidMount() {
		// Fetch App Content Card when this page loads
		raiseAction(new WebGivingUserAction.FetchAppContentCard(), this.props);
	}

	render() {
		const { transitionState, vm } = this.props;
		const {
			amount,
			paymentLabel: { VerbSentenceCase },
			isLoading,
			isBlocked,
			pageShouldBeVisible,
			brandingPackage,
			merchantInfo,
			showsOnlyAmount,
			disableValidation,
			givingType,
			givingTypeOptions,
			hasLineItems,
		} = vm;
		const { MerchantCryptoLink } = merchantInfo;
		const elementsShouldEnter = transitionState === 'entered';
		const hasCustomGivingTitle = !!(brandingPackage && brandingPackage.GivingTitle);
		const hasEnhancedTradingName = !!(brandingPackage && brandingPackage.EnhancedLogoImageUrl);
		const hasCryptoGiving = !!MerchantCryptoLink;

		const isSplitGivingEnabled = merchantInfo.SplitGivingEnabled;
		const headerClasses = classNames(style.header, {
			[style.headerHasEnhancedTradingName]: hasEnhancedTradingName,
			[style.headerLifted]: amount.hasValidationError && hasCryptoGiving
				|| (isSplitGivingEnabled && hasLineItems),
		});
		const givingTitleClasses = classNames(style.heading, {
			[style.givingTitle]: hasCustomGivingTitle,
		});

		const showGivingTypeDropdown = givingTypeOptions.length > 1;

		return (
			<div
				className={getPageClasses(pageShouldBeVisible, isLoading)}
				data-pp-at-target={pageShouldBeVisible ? `Give page` : null}
			>
				<Form onSubmit={this.handleSubmit}>
					<div
						className={
							hasCryptoGiving
								? classNames(this.getStyleBasedOnShow(showsOnlyAmount, 'panel-body panel-body-form'), {
									[style.panelHasHeader]: hasCryptoGiving,
								})
								: this.getStyleBasedOnShow(showsOnlyAmount, 'panel-body panel-body-form')
						}
						ref={this.savePanelBodyNode}
					>
						<FadeTransition in={elementsShouldEnter} onExit={this.setContentHeight}>
							<div>
								<header className={headerClasses}>
									{hasEnhancedTradingName && (
										<h2
											className={style.enhancedTradingName}
											data-pp-at-target="Merchant enhanced trading name"
										>
											{merchantInfo.TradingName}
										</h2>
									)}
									<h1 className={givingTitleClasses} data-pp-at-target="Giving title">
										{/* If not for typescript misinterpreting types, this would be:
											{hasCustomGivingTitle ? brandingPackage.GivingTitle : VerbSentenceCase}
										*/}
										{(brandingPackage && brandingPackage.GivingTitle) || (
											<Text>{VerbSentenceCase}</Text>
										)}
									</h1>
									<Amount
										amount={amount}
										acceptanceTestTargetId="Amount input"
										disableValidation={disableValidation}
										containerClass={hasCryptoGiving ? style.amountContainer : ''}
										hasCryptoGiving={hasCryptoGiving}
										hasLineItems={hasLineItems}
									/>
								</header>
								<div
									className={this.getStyleBasedOnShow(showsOnlyAmount, 'panel-body-content')}
									ref={this.saveContentNode}
								>
									<GivingOptionsView givingType={givingType} givingTypeOptions={givingTypeOptions} showGivingTypeDropdown={showGivingTypeDropdown} vm={vm}></GivingOptionsView>
								</div>
							</div>
						</FadeTransition>
					</div>
					<PanelFooter className={pageFooter}>
						{showGivingTypeDropdown
							? (this.getRedirectButton(givingType))
							: (<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}>
								<Text>Next</Text>
							</SubmitButton>)}
					</PanelFooter>
				</Form>
			</div>
		);
	}


	getRedirectButton = (type: FormField<GivingType>) => {
		switch (type.value) {
			case GivingType.CardorACH:
				return <SubmitButton acceptanceTestTargetId="Next" isLoading={this.props.vm.isLoading} isBlocked={this.props.vm.isBlocked}>
							<Text>Next</Text>
						</SubmitButton>
			case GivingType.Cryptocurrency:
				return <Link
					className={classNames(getBrandedButtonClass(ButtonStyle.Primary), style.btn)}
					acceptanceTestTargetId="Crypto Redirect"
					branded={false}
					openInNewTab={true}
					href={this.props.vm.merchantInfo.MerchantCryptoLink}
					onClick={() => raiseAction(new WebGivingUserAction.LogCryptoGiving(), this.props)}
				>
					<Text>{`Make your Crypto ${this.props.vm.paymentLabel.NounLowerCase}`}</Text>
				</Link>
			case GivingType.Stock:
				return <Link
					className={classNames(getBrandedButtonClass(ButtonStyle.Primary), style.btn)}
					acceptanceTestTargetId="Stock Redirect"
					branded={false}
					openInNewTab={true}
					href={this.props.vm.merchantInfo.MerchantStockGivingLink}
					onClick={() => raiseAction(new WebGivingUserAction.LogStockGiving(), this.props)}
				>
					<Text>{`Make your Stock ${this.props.vm.paymentLabel.NounLowerCase}`}</Text>
				</Link>
			default:
		}
	}
	getStyleBasedOnShow = (showsOnlyAmount: boolean, styleWhenShow: string) => (showsOnlyAmount ? '' : styleWhenShow);

	setContentHeight = () => (this.panelBodyNode.style.minHeight = `${this.contentNode.offsetHeight}px`);

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => (this.panelBodyNode = panelBodyNode);

	private saveContentNode = (contentNode: HTMLDivElement) => (this.contentNode = contentNode);

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	};
}

export interface GivingOptionsViewProps {
	givingType: FormField<GivingType>;
	givingTypeOptions: { value: GivingType; label: string }[];
	vm: PageGiveViewModel;
	showGivingTypeDropdown: boolean;
}

@observer
export class GivingOptionsView extends React.Component<GivingOptionsViewProps> {
	render() {
		const { givingType, givingTypeOptions, vm: { paymentLabel }, vm, showGivingTypeDropdown } = this.props;

		let content;

		switch (givingType.value) {
			case GivingType.CardorACH:
				content = <PageGiveDollars vm={vm} />;
				break;
			case GivingType.Cryptocurrency:
				content = <PageGiveOther vm={vm} givingType={GivingType.Cryptocurrency} />;
				break;
			case GivingType.Stock:
				content = <PageGiveOther vm={vm} givingType={GivingType.Stock} />;
				break;
			default:
				content = <PageGiveDollars vm={vm} />;
				break;
		}

		let label = `How would you like to ${paymentLabel.VerbLowerCase}?`

		return (
			<div>
				{showGivingTypeDropdown && <FormControl className={style.givingOption} label={label}
				>
					<SelectField
						formField={givingType}
						options={givingTypeOptions}
						acceptanceTestTargetId="Recurring End Type"
					/>
				</FormControl>}
				{content}
			</div>

		);
	}
}

export enum GivingType {
	CardorACH = 0, Cryptocurrency = 1, Stock = 2,
}
export interface IOneTimePaymentFieldsProps {
	oneTimeGiftDate: FormField<OptionalDate>;
	minStartDate: Date;
	minEndDate: Date;
	maxDate: Date;
}

@observer
export class OneTimePaymentFields extends React.Component<IOneTimePaymentFieldsProps, any> {
	render() {
		const { oneTimeGiftDate, minStartDate, maxDate } = this.props;
		return (
			<div className="row">
				<FormControl
					label="When"
					validationError={oneTimeGiftDate.validationError}
					className={'col-sm-6 ' + style.noRightPaddingOnDesktop}
				>
					<DatePickerField
						formField={oneTimeGiftDate}
						options={{ minDate: minStartDate, maxDate: maxDate }}
						acceptanceTestTargetId="One time gift date"
					/>
				</FormControl>
			</div>
		);
	}
}

export interface IRecurringPaymentFieldsProps {
	payerSpecifiedRecurringEndDateEnabled: boolean;
	frequency: FormField<FrequencyCode>;
	frequencyOptions: { value: FrequencyCode; label: string }[];
	startDate: FormField<OptionalDate>;
	endType: FormField<EndType>;
	endTypeOptions: { value: EndType; label: string }[];
	endDate: FormField<OptionalDate>;
	endOccurrences: FormField<number | null>;
	minStartDate: Date;
	minEndDate: Date;
	maxDate: Date;
	paymentLabel: PaymentLabel;
	handleFrequencyChange: (x: any) => void;
	handleStartDateChange: (d: OptionalDate) => void;
	yearlyRecurringGivingEnabled?: boolean;
	semiYearlyRecurringGivingEnabled?: boolean;
	quarterlyRecurringGivingEnabled?: boolean;
}

@observer
export class RecurringPaymentFields extends React.Component<IRecurringPaymentFieldsProps, any> {
	render() {
		const {
			yearlyRecurringGivingEnabled,
			semiYearlyRecurringGivingEnabled,
			quarterlyRecurringGivingEnabled,
			frequency,
			frequencyOptions,
			startDate,
			minStartDate,
			maxDate,
			payerSpecifiedRecurringEndDateEnabled,
			handleFrequencyChange,
			handleStartDateChange,
		} = this.props;

		let shouldShowSplitButtonDropdownCombo = NewFeatures.WebGiving_AddTestOnlyRecurringPaymentFrequencies
			? frequencyOptions.length > 4
			: Boolean(yearlyRecurringGivingEnabled || semiYearlyRecurringGivingEnabled || quarterlyRecurringGivingEnabled);

		const renderRecurringFrequencyButtons = () => {
			if (shouldShowSplitButtonDropdownCombo) {
				return (
					<FormControlFrequencyComboButton
						field={frequency}
						options={frequencyOptions}
						ariaLabel="Frequency"
						acceptanceTestTargetId="Gift Frequency"
						onChange={handleFrequencyChange}
					/>
				);
			} else {
				return (
					<FormControlSplitButton
						field={frequency}
						options={frequencyOptions}
						uniformWidth={true}
						ariaLabel="Frequency"
						acceptanceTestTargetId="Gift Frequency"
						onChange={handleFrequencyChange}
					/>
				);
			}
		};

		return (
			<div>
				<FormControl label="Frequency" validationError={frequency.validationError}>
					{renderRecurringFrequencyButtons()}
				</FormControl>
				<div className="row">
					<FormControl
						label="Starting"
						validationError={startDate.validationError}
						className={'col-sm-6 ' + style.noRightPaddingOnDesktop}
					>
						<DatePickerField
							formField={startDate}
							options={{ minDate: minStartDate, maxDate: maxDate, disableDayFn: this.disableDays }}
							acceptanceTestTargetId="Recurring Start Date"
							onChange={handleStartDateChange}
						/>
					</FormControl>
				</div>
				{payerSpecifiedRecurringEndDateEnabled && <RecurringEndInputs {...this.props} />}
			</div>
		);
	}

	disableDays: (date: Date) => boolean = (date) => {
		if (this.props.frequency.value === FrequencyCode.FirstAndFifteenth) {
			return date.getDate() !== 1 && date.getDate() !== 15;
		}
		return false;
	};
}

@observer
export class RecurringEndInputs extends React.Component<IRecurringPaymentFieldsProps, any> {
	render() {
		const { endType, endTypeOptions, endDate, endOccurrences, minEndDate, maxDate, paymentLabel } = this.props;
		const { NounPluralLowerCase } = paymentLabel;
		let containerClass, endingClass, detailsInput;
		switch (endType.value) {
			case EndType.Occurrences:
				containerClass = style.recurringEnd;
				endingClass = style.recurringEndType;
				detailsInput = (
					<FormControl
						label={`Number of ${NounPluralLowerCase}`}
						className={style.recurringEndDetails}
						validationError={endOccurrences.validationError}
					>
						<FormControlNumber
							formField={endOccurrences}
							maxLength={4}
							min={1}
							acceptanceTestTargetId="Ending Number of Occurrences"
							placeholder="e.g. 1-1000"
						/>
					</FormControl>
				);
				break;
			case EndType.Date:
				containerClass = style.recurringEnd;
				endingClass = style.recurringEndType;
				detailsInput = (
					<FormControl
						label="End date"
						className={style.recurringEndDetails}
						validationError={endDate.validationError}
					>
						<DatePickerField
							formField={endDate}
							options={{ minDate: minEndDate, maxDate: maxDate }}
							acceptanceTestTargetId="Recurring End Date"
							placeholder="Select end date"
						/>
					</FormControl>
				);
				break;
			default:
				containerClass = 'row';
				endingClass = 'col-sm-6 ' + style.noRightPaddingOnDesktop;
				detailsInput = null;
				break;
		}

		return (
			<div className={containerClass}>
				<FormControl label="Ending" className={endingClass}>
					<SelectField
						formField={endType}
						options={endTypeOptions}
						acceptanceTestTargetId="Recurring End Type"
					/>
				</FormControl>
				{detailsInput}
			</div>
		);
	}
}

@observer
class Amount extends React.Component<{
	amount: FormField<number>;
	acceptanceTestTargetId: string;
	disableValidation?: boolean;
	containerClass?: string;
	hasCryptoGiving?: boolean;
	hasLineItems?: boolean;
}> {
	onBlur = () => {
		const { amount, disableValidation } = this.props;
		if (!disableValidation) {
			amount.validateEveryTimeCauseImASpecialCase();
		}
	};

	renderAmountWrapper = (formattedAmount: string, hasLineItems: boolean = false) => {
		const wrapperClassName = classNames(hasLineItems ? style.wrapperWithoutBorder : style.wrapper, this.props.amount.value === 0 && style.zero);
		return (
			<div className={style.decorator}>
				<span className={wrapperClassName}>
					<span className={classNames(style.dollar)} aria-hidden="true" role="presentation">
						$
					</span>
					<span className={style.underline}>{formattedAmount}</span>
				</span>
			</div>
		);
	};

	renderInput = () => {
		const { amount, acceptanceTestTargetId, hasLineItems } = this.props;
		const formattedAmount = Formatter.formatNumberForDisplay(amount.value, 2);
		return (
			<>
				<FocusOnMount delay={fadeTransitionDuration}>
					<OriginalPassiveAmountInput
						value={amount.value}
						dataFieldInvalid={amount.hasValidationError}
						onChange={amount.updateValue}
						onBlur={this.onBlur}
						className={style.input}
						acceptanceTestTargetId={acceptanceTestTargetId}
						ariaDescribedBy="amount-validation-error"
						ariaInvalid={amount.hasValidationError}
						ariaRequired={true}
						amountIsLocked={hasLineItems || amount.isLocked}
					/>
				</FocusOnMount>
				{this.renderAmountWrapper(formattedAmount, hasLineItems)}
			</>
		);
	};

	render() {
		const { amount, containerClass, hasCryptoGiving, hasLineItems } = this.props;
		const formattedAmount = Formatter.formatNumberForDisplay(amount.value, 2);
		const errorClass = classNames({
			[style.amountValidationError]: amount.hasValidationError,
		});

		return (
			<div className={hasCryptoGiving ? `form-control-container ${containerClass}` : 'form-control-container'}>
				<div className={`${style.amount} ${getSizeName(formattedAmount)}`}>
					{this.renderInput()}
				</div>
				{hasLineItems && (
					<div className={style.yourTotal}>
						<Text>Your total</Text>
					</div>
				)}
				<div id="amount-validation-error" className={errorClass} role="alert" aria-atomic="true">
					{amount.hasValidationError && (
						<SvgIcon
							svg={errorSvg}
							className={style.errorIcon}
							ariaLabel="There is a problem with the amount field:"
						/>
					)}
					{amount.hasValidationError && (
						<div className={style.errorContent}>
							{!!amount.validationError ? (
								<Text params={amount.getTranslationParams()}>{amount.validationError}</Text>
							) : (
								amount.validationError
							)}
						</div>
					)}
				</div>
			</div>
		);
	}
}
